import { SUPPORTED_MACHINE_TYPE_FAMILIES, platformChosenBootDiskType } from "./machine-type-catalog.js";
import { z } from "zod";
//#region src/compute-class.ts
/**
* The FIVE app-facing compute classes (model note §4c FINAL).
*
* - `N` standard — the DEFAULT and the floor. Balanced.
* - `C` compute  — high per-core performance, latest silicon. MUST be declared explicitly:
*                  per-core-performance intent is not derivable from a resource vector.
* - `M` memory   — the only class permitting more than {@link MEM_MIB_PER_VCPU_MAX_N_C} MiB per
*                  vCPU. DERIVABLE: a vector above that ratio under class `N` auto-selects `M`
*                  with a surfaced note (see {@link resolveMachineType}). Declaring `M`
*                  explicitly is permitted as intent documentation.
* - `gpu-inference` — serving, ≤ {@link MAX_GPU_COUNT} GPUs, cost per token. MUST be explicit.
* - `gpu-training`  — multi-GPU, NVLink, FIXED GPU-count shapes. MUST be explicit, and takes a
*                  GPU COUNT instead of a cpu/mem vector (the vector is meaningless: GPU count
*                  is fixed per shape on every training SKU on both clouds).
*
* ⛔ `E` (economy) IS DELIBERATELY ABSENT and must not be re-added. It was killed by ruling
* (model note §4c FINAL: "shared and unpredictable", and the E2↔T-burstable equivalence was the
* weakest cross-cloud mapping we had — burstable-credit semantics differ). ⚠️ Killing `E` from
* the APP-FACING set does NOT evict `e2` from the platform's own Layer-A pools: infra,
* default-app and base pools stay platform-sized and class-ungoverned, so there is no fleet cost
* jump. If you are here because someone wants a cheap class, the answer is a smaller VECTOR
* under `N`, not a sixth class.
*
* ⚠️ ORDER IS CONTRACT-VISIBLE only in error messages (it is joined into a
* `unknown-class` detail). It is NOT a preference order and nothing resolves by position.
*/
const COMPUTE_CLASSES = [
	"N",
	"C",
	"M",
	"gpu-inference",
	"gpu-training"
];
/** zod enum over {@link COMPUTE_CLASSES}. The manifest has no zod (the Ajv schema is its door),
*  so this exists for the WORKER wire and for CP DTOs. */
const computeClassSchema = z.enum(COMPUTE_CLASSES);
/**
* The class an omitted `class` means (model note §4c: "`class` is OPTIONAL, defaults to `N`").
*
* ⛔ THIS IS NOT THE SAME AS "the operator resolves an undeclared unit through the resolver".
* See the D10 warning on {@link resolveMachineType} — a unit that declares NOTHING must skip
* resolution entirely.
*/
const DEFAULT_COMPUTE_CLASS = "N";
/** True iff `c` is a known {@link ComputeClass}. Total over any input (the value is
*  app-authored and reaches us as `unknown`). */
function isComputeClass(c) {
	return typeof c === "string" && COMPUTE_CLASSES.includes(c);
}
/** The two GPU classes. A `gpuCount` and an `accelerator` pin are meaningful ONLY on these. */
const GPU_COMPUTE_CLASSES = ["gpu-inference", "gpu-training"];
/** True iff `c` is one of {@link GPU_COMPUTE_CLASSES}. */
function isGpuComputeClass(c) {
	return typeof c === "string" && GPU_COMPUTE_CLASSES.includes(c);
}
/**
* Tenant sizes (model note §4b AMENDMENT). TRANSPARENT names: the multiplier IS the enum value,
* so there is no opaque `t|s|m|lg|xlg` vocabulary and no translation table for a human to own.
* Extensible UPWARD (`16x`, `32x`…) — adding a member is a two-line change here plus the Ajv
* enum twin.
*
* `.5x` is what `tiny` became: the platform-controlled floor for dev/internal tenants.
* The DEFAULT is {@link DEFAULT_SIZE} = `1x`, which is byte-identical to today (D10).
*/
const SIZES = [
	".5x",
	"1x",
	"2x",
	"4x",
	"8x"
];
/** zod enum over {@link SIZES}. */
const sizeSchema = z.enum(SIZES);
/** The size a tenant with no declared size, and a `sizing.mode: 'fixed'` with no `size`, both
*  mean. `1x` ⇒ multiplier 1 ⇒ the effective vector equals the declared vector (D10). */
const DEFAULT_SIZE = "1x";
/**
* ⛔ THE EXPLICIT ENUM→MULTIPLIER MAP (D4, Dave's second amendment). NEVER derive a multiplier
* by parsing the label.
*
* The label and the number LOOK redundant, and that redundancy is the point: parsing `'.5x'` to
* `0.5` makes the semantics stringly-typed, so the day someone adds `'16x'`, or a size named
* `'lg'` returns, or a label is typo'd to `'2X'`, a parser produces a plausible wrong number
* instead of a compile error. This map makes a new size a TYPE error at every consumer until it
* is given a value.
*
* ⚠️ S-3/S-4 of the model note survive structurally: these values remain operator-adjustable in
* principle (the map is the seam an operator-owned table would replace), and the per-app size
* override — an enum value, clamped by Layer-B limits — stands. Only the NAMES changed.
*/
const SIZE_MULTIPLIERS = {
	".5x": .5,
	"1x": 1,
	"2x": 2,
	"4x": 4,
	"8x": 8
};
/** True iff `s` is a known {@link Size}. Total over any input. */
function isSize(s) {
	return typeof s === "string" && SIZES.includes(s);
}
/**
* The multiplier for `s`, or `null` when `s` is not a {@link Size}.
*
* ⛔ GUARD-THEN-INDEX, deliberately. {@link SIZE_MULTIPLIERS} is an object literal, so a raw
* `SIZE_MULTIPLIERS[untrusted]` can resolve an `Object.prototype` member name
* (`'constructor'`, `'toString'`) to a FUNCTION and hand a caller something it will multiply by.
* `isSize` first makes that unreachable — the same reasoning `parseK8sQuantity` records for
* using a `Map`, applied to a `Record` that D4 requires be a `Record`.
*
* ⇒ Callers that hold a `Size` at the type level should read {@link SIZE_MULTIPLIERS} directly;
* this accessor is for values that crossed a wire.
*/
function multiplierForSize(s) {
	return isSize(s) ? SIZE_MULTIPLIERS[s] : null;
}
/** Per-fixture sizing modes (model note §4b S-2). `tenant-scaled` ⇒ the operator multiplies the
*  declared vector by the tenant multiplier (summed over resident tenants for a SHARED fixture,
*  S-1). `fixed` ⇒ the fixture's own static size, defaulting to {@link DEFAULT_SIZE}. */
const SIZING_MODES = ["tenant-scaled", "fixed"];
/**
* A fixture unit's `sizing` block. `.strict()` + a `.superRefine` mirroring
* `$defs/fixtureSizing`'s Ajv `if/then` — the DD-1 pattern from `scaling-intent.ts`.
*
* CROSS-FIELD RULE: `size` requires `mode: 'fixed'`. Under `tenant-scaled` the multiplier comes
* from the TENANT, so a declared `size` would be silently ignored — and a field that parses and
* does nothing is the exact defect class this whole program exists to remove (the model note's
* §2.4 "one-way ratchet ~8 fields ahead of the runtime").
*/
const fixtureSizingSchema = z.object({
	mode: z.enum(SIZING_MODES),
	size: sizeSchema.optional()
}).strict().superRefine((v, ctx) => {
	if (v.mode === "fixed") return;
	if (v.size !== void 0) ctx.addIssue({
		code: z.ZodIssueCode.custom,
		path: ["size"],
		message: "sizing.size requires sizing.mode: 'fixed' (under 'tenant-scaled' the multiplier comes from the tenant, so a declared size would be ignored)"
	});
});
/** The sizing an omitted `sizing` block means (D3): fixed at {@link DEFAULT_SIZE}. Frozen so a
*  consumer cannot mutate the shared default. */
const DEFAULT_FIXTURE_SIZING = Object.freeze({
	mode: "fixed",
	size: "1x"
});
/**
* CPU architecture, a TRAIT rather than a class (model note §4c): AWS ships Graviton in every
* family letter while GCP has Axion only in C4A/N4A, so "arm" cannot be a class on both clouds —
* but a trait can honestly report "unavailable here" ({@link MachineTypeRefusalReason}
* `arch-unavailable`).
*
* ⚠️ `spot` and `local-ssd` are ALSO traits and are deliberately NOT here: `spot` already lives
* on `scalingIntent.nodeShape.spot` and local-SSD availability varies per generation (C4* needs
* explicit `-lssd` machine types; N4D and H3 have none), which makes it a machine-type-selection
* concern for a later change, not a resolver input today. Do not add either here.
*/
const COMPUTE_ARCHES = ["x86", "arm64"];
/** zod enum over {@link COMPUTE_ARCHES}. */
const computeArchSchema = z.enum(COMPUTE_ARCHES);
/** True iff `a` is a known {@link ComputeArch}. Total over any input. */
function isComputeArch(a) {
	return typeof a === "string" && COMPUTE_ARCHES.includes(a);
}
/**
* The GCP machine-type FAMILIES that are arm64 (Axion). Everything else in every ladder is x86.
*
* ⛔ TWO-DIRECTIONAL: an `arch: 'arm64'` ask keeps ONLY these families, and an `arch: 'x86'` ask
* REMOVES them. The second half matters more than it looks — a C-class x86 workload resolved
* onto `c4a` would pull an amd64 image onto an arm node and CrashLoopBackOff with an `exec
* format error`, which reads as an app bug.
*/
const ARM64_MACHINE_FAMILIES_GCP = ["c4a", "n4a"];
/** O(1) membership for the arch filter. Module-level so the Set is built once. */
const ARM64_FAMILY_SET = new Set(ARM64_MACHINE_FAMILIES_GCP);
/**
* GCP family ladders per class, NEWEST GENERATION FIRST (model note §4c; D5 "Generation
* preference = ladder order (newest first with availability)").
*
* The resolver walks this list in order and stops at the FIRST family that has a satisfying
* shape in the catalog — so generation preference dominates shape size, which is what "the
* platform always picks the generation itself" means. Legacy generations are reachable only as
* the last rungs and are never nameable by an app.
*
* ⚠️ MEASURED AVAILABILITY IS PATCHY AND THAT IS WHY `class-unavailable-in-region` EXISTS:
* `us-west2-a` has NO memory family and NO GPU family at all; `c4n` existed only in
* `us-central1` of four sampled regions; `g4` only in `europe-west4`. A ladder entry is a
* PREFERENCE, never a promise.
*
* Frozen at both levels, the record and each rung list: the resolver reads this record on every
* call, so a consumer that pushes to a rung list or replaces a class key gets a `TypeError`. It
* cannot change the family preference of a later resolution.
*/
const MACHINE_TYPE_FAMILY_LADDERS_GCP = Object.freeze({
	N: Object.freeze([
		"n4",
		"n4d",
		"n4a",
		"n2",
		"n2d"
	]),
	C: Object.freeze([
		"c4",
		"c4d",
		"c4n",
		"c4a",
		"c3",
		"c3d"
	]),
	M: Object.freeze([
		"m4",
		"m4n",
		"m3"
	]),
	"gpu-inference": Object.freeze(["g4", "g2"]),
	"gpu-training": Object.freeze([
		"a4x",
		"a4",
		"a3"
	])
});
/**
* AWS family ladders per class, newest first (plan `2026-10-14-aws-dp-parity`, D9).
*
* What it is for. App pools on an AWS data plane resolve a machine type from the pool class and its
* resource vector, as on GKE. The operator and provisioning-svc pass this record to
* {@link resolveMachineType} as `options.ladders`, with {@link awsMachineTypeFamily} as
* `options.family`.
*
* The CPU rungs are the six x86_64 families of {@link AWS_MACHINE_TYPE_FAMILIES}, and the GPU rungs
* are the six x86_64 families of {@link GPU_MACHINE_FAMILIES_AWS}
* (2026-10-24-pool-machine-generations, WS-4a, D-16). Every AWS node group runs an x86_64 AMI, so
* no arm64 family is a rung: `p6e-gb200` (GB200 on a Grace CPU) is absent.
*
* - `N`: `m7i`, then `m6i` (4 GiB per vCPU).
* - `C`: `c7i`, then `c6i` (2 GiB per vCPU).
* - `M`: `r7i`, then `r6i` (8 GiB per vCPU).
* - `gpu-inference`: `g6e` (L40S), then `g6` (L4).
* - `gpu-training`: `p6-b200` (B200), then `p5en` and `p5e` (H200), then `p5` (H100).
*
* Invariants: every rung is lowercase, and no family is in two classes. A rung may hold a hyphen
* (`p6-b200`): it is the text {@link awsMachineTypeFamily} returns for a node type of that family,
* and that function reads to the last hyphen, so `p6-b200-48xlarge` gives `p6-b200`.
*
* Frozen at both levels, as {@link MACHINE_TYPE_FAMILY_LADDERS_GCP}: a consumer that pushes to a
* rung list or replaces a class key gets a `TypeError`. A caller that needs another ladder builds
* a new record and passes it as `options.ladders`.
*/
const MACHINE_TYPE_FAMILY_LADDERS_AWS = Object.freeze({
	N: Object.freeze(["m7i", "m6i"]),
	C: Object.freeze(["c7i", "c6i"]),
	M: Object.freeze(["r7i", "r6i"]),
	"gpu-inference": Object.freeze(["g6e", "g6"]),
	"gpu-training": Object.freeze([
		"p6-b200",
		"p5en",
		"p5e",
		"p5"
	])
});
/**
* The ladders keyed by CLOUD — a **derived VIEW** over the two per-cloud constants above (D46(a)).
*
* ⭐ WHY THIS EXISTS AT ALL: `40-ui.md` needs `ladders[cloud][class]` to render the class-catalog
* screen, and its first draft assembled that map LOCALLY from two imports. A local `Record` over
* contracts DATA drifts exactly the way a local Record over a contracts ENUM does — it is the same
* defect one door over, and the console and the resolver would then be able to disagree about which
* generation the platform prefers. One source, one map.
*
* ⛔ IT IS A VIEW, NOT A COPY. The two suffixed constants remain the per-cloud TRUTHS; this map only
* points at them. `A-19b` pins **referential identity** (`===`), NOT deep equality — a deep-equality
* test would pass on a hand-retyped duplicate, which is precisely the failure being prevented. If you
* ever find yourself editing a family list here rather than in the constant above, stop: the edit
* belongs upstream and this map will pick it up for free.
*
* ⚠️ Keyed by the two clouds that HAVE ladders, not by every provider the platform knows: `local`
* (k3d) has no machine-type families at all, and `azure` has no researched ladder. A
* `Record<CloudProvider, …>` would therefore need two dishonest empty entries, and an empty ladder
* reads as "this class is unavailable" rather than "this cloud is not modelled". Widen the key set
* when a real ladder exists, never before.
*
* The `aws` entry is the ladder that app pools on AWS resolve against (D9 of plan
* `2026-10-14-aws-dp-parity`). Since WS-4a of 2026-10-24-pool-machine-generations, no ladder of
* either cloud is empty.
*
* Frozen: a consumer that replaces the `gcp` or `aws` member gets a `TypeError`. The members are
* the two frozen per-cloud records themselves.
*/
const MACHINE_TYPE_FAMILY_LADDERS = Object.freeze({
	gcp: MACHINE_TYPE_FAMILY_LADDERS_GCP,
	aws: MACHINE_TYPE_FAMILY_LADDERS_AWS
});
/**
* The AWS machine families of the static AWS pool, sorted (D-18).
*
* What it is for. A pool tier on `aws` names one of these families, and both publish doors check
* the name against this list through {@link TIER_MACHINE_FAMILIES}. From stage S1b (WS-1c, D-18),
* provisioning-svc builds its static pool (`AWS_MACHINE_TYPE_POOL`) from these names, so the
* publish check and the pool that a data plane enables read one list. Until WS-1c lands,
* provisioning-svc keeps its own `FAMILIES` table in `aws-machine-type-pool.ts`.
*
* The six families are x86_64: every AWS CPU node group runs the AMI `AL2023_x86_64_STANDARD`.
* They are the CPU rungs of {@link MACHINE_TYPE_FAMILY_LADDERS_AWS}. The GPU families of AWS are a
* separate list, {@link GPU_MACHINE_FAMILIES_AWS}, and a test holds the ladder rungs equal to the
* union of the two lists as sets. This list stays CPU only for two reasons. A CPU tier names a
* family from it, and a GPU family there would place a pod that asks for no GPU on a GPU machine.
* And provisioning-svc keys its CPU pool table by {@link AwsMachineTypeFamily}. Frozen: the publish
* rules read it through {@link TIER_MACHINE_FAMILIES}, so a consumer that mutates it gets a
* `TypeError`.
*/
const AWS_MACHINE_TYPE_FAMILIES = Object.freeze([
	"c6i",
	"c7i",
	"m6i",
	"m7i",
	"r6i",
	"r7i"
]);
/**
* The GCP families of {@link SUPPORTED_MACHINE_TYPE_FAMILIES} whose machine types carry a GPU,
* sorted: `a2` (A100), `a3` (H100, H200), `a4` (B200), `a4x` (GB200), `g2` (L4) and `g4` (RTX PRO
* 6000). A pool tier reaches a GPU through `accelerator` and the accelerator table, never through
* a family name. So a tier that names one of these as `family` is refused: its pods would ask for
* no GPU on a GPU machine. Frozen.
*/
const GPU_MACHINE_FAMILIES_GCP = Object.freeze([
	"a2",
	"a3",
	"a4",
	"a4x",
	"g2",
	"g4"
]);
/**
* The AWS machine families whose instance types carry an NVIDIA GPU and run on x86_64, sorted
* (2026-10-24-pool-machine-generations, WS-4a, D-10, D-44): `g6` (L4), `g6e` (L40S), `p5` (H100),
* `p5e` and `p5en` (H200) and `p6-b200` (B200). The AWS twin of {@link GPU_MACHINE_FAMILIES_GCP}.
*
* What it is for. These are the GPU rungs of {@link MACHINE_TYPE_FAMILY_LADDERS_AWS} and the
* families of the AWS entries of `ACCELERATOR_TABLE`. A pool tier reaches one through
* `accelerator`, never through `family`: no member is in {@link TIER_MACHINE_FAMILIES}, so a CPU
* tier that names `p5` is refused `pool-tier-family-unknown`. provisioning-svc builds its AWS GPU
* pool entries from these names (WS-4d).
*
* Each member is the text {@link awsMachineTypeFamily} returns for an instance type of the family,
* so `p6-b200` holds a hyphen. `p6e-gb200` (arm64) and `p4d`/`p4de` (A100, no model in the table)
* are absent. Frozen: a consumer that mutates it gets a `TypeError`.
*/
const GPU_MACHINE_FAMILIES_AWS = Object.freeze([
	"g6",
	"g6e",
	"p5",
	"p5e",
	"p5en",
	"p6-b200"
]);
/**
* The CPU machine families a pool tier with `family` may name, per cloud (D-15, D-18).
*
* - `gcp`: {@link SUPPORTED_MACHINE_TYPE_FAMILIES} without {@link GPU_MACHINE_FAMILIES_GCP}, in the
*   order of that list.
* - `aws`: {@link AWS_MACHINE_TYPE_FAMILIES}, which holds no member of
*   {@link GPU_MACHINE_FAMILIES_AWS}.
*
* The GCP list is a grammar list: it says which names Zion knows, and the data plane's enabled
* catalog decides what it can place. A tier whose family a data plane does not enable is dropped
* there and reported (overview section 3.1, rule 3). Frozen.
*/
const TIER_MACHINE_FAMILIES = Object.freeze({
	gcp: Object.freeze(SUPPORTED_MACHINE_TYPE_FAMILIES.filter((f) => !GPU_MACHINE_FAMILIES_GCP.includes(f))),
	aws: AWS_MACHINE_TYPE_FAMILIES
});
/**
* True when `family` is a member of {@link TIER_MACHINE_FAMILIES} for `cloud`. Total over any
* input: an unknown cloud, a non-string and a prototype name such as `'constructor'` answer
* `false`. The match is exact and case-sensitive. Pure.
*/
function isTierMachineFamily(cloud, family) {
	if (cloud !== "gcp" && cloud !== "aws" || typeof family !== "string") return false;
	return TIER_MACHINE_FAMILIES[cloud].includes(family);
}
/**
* The families each cloud documents as never sold as Spot capacity (D-28).
*
* - `gcp`: `a4x` (GB200) and `x4` (bare metal only). GCP sells no Spot VM on bare metal.
* - `aws`: none. AWS documents no family as never Spot, its GPU families included.
*
* Publish refuses a `spot` tier on these families only. For every other family the tier
* controller tries, and a refusal sends the tier to backoff (D-3). Frozen.
*/
const NEVER_SPOT_MACHINE_FAMILIES = Object.freeze({
	gcp: Object.freeze(["a4x", "x4"]),
	aws: Object.freeze([])
});
/**
* True when `cloud` documents `family` as never Spot ({@link NEVER_SPOT_MACHINE_FAMILIES}). Total
* over any input: an unknown cloud or a non-string family answers `false`. Pure.
*/
function isNeverSpotFamily(cloud, family) {
	if (cloud !== "gcp" && cloud !== "aws" || typeof family !== "string") return false;
	return NEVER_SPOT_MACHINE_FAMILIES[cloud].includes(family);
}
/**
* The maximum memory-to-vCPU ratio classes `N` and `C` can serve, in MiB per vCPU (8 GiB).
*
* This single number is what makes `M` DERIVABLE: no `N` or `C` shape on GCP sells more than
* 8 GB per vCPU (the `-highmem` shape of a family IS the 8 GB/vCPU rung), so a vector above this
* ratio is served by a shape carrying proportionally MORE CPU than the ask — the vector is a
* MINIMUM, so a high RATIO alone never exhausts a class (`1000m / 100 GiB` under `C` resolves to
* `c4-highmem-16`; A-39). What no `N`/`C` shape OFFERS is that RATIO; the ASK is still servable.
* ⛔ And the derivation is FROM `N` ONLY: an explicitly declared `C` above this ratio STAYS `C`
* (D15 — declared intent is never discarded) and refuses `vector-exceeds-class` only when no `C`
* shape covers BOTH dimensions. Under `N` (declared or defaulted) the resolver selects `M` with a
* surfaced note (`derived-class-m`) instead of
* refusing. Below it, no derivation happens and `N` stays `N`.
*/
const MEM_MIB_PER_VCPU_MAX_N_C = 8192;
/**
* The largest `gpuCount` the platform admits from a manifest.
*
* Bounded because the Ajv door is the ONLY bound before this value reaches the resolver, and an
* unbounded integer there is a request nothing can ever satisfy that costs a full catalog walk
* to refuse. `8` covers every shipped GCP shape (gpu-inference is ≤8 GPUs by the class
* definition; a3/a4/a4x training shapes are 4- and 8-GPU). Widening it is a one-line change
* HERE plus the Ajv `maximum` twin — guessing wide is not.
*/
const MAX_GPU_COUNT = 8;
/**
* The GCP machine-type FAMILY of `nodeType` — the substring up to the first `-`
* (`c4-highmem-32` → `c4`; `c4d-standard-32-lssd` → `c4d`). A string with no `-` is the whole
* family. Lowercased, for the same one-directional defensive reason
* `bootDiskTypeForMachineType` records: the only failure that matters is a MISS, and a stray
* uppercase variant is the cheapest way to get one.
*
* ⭐ TOTAL BY CONTRACT — accepts any string (empty, no dash, absurd length, unicode, control
* characters) and never throws. It is a pure string operation with no allocation beyond the
* slice, so it is also ReDoS-free by construction (no regex).
*
* GCP only. An AWS node type uses {@link awsMachineTypeFamily}: `m7i-flex-large` has the family
* `m7i-flex`, which this function reads as `m7i`.
*/
function gcpMachineTypeFamily(nodeType) {
	const dash = nodeType.indexOf("-");
	return (dash === -1 ? nodeType : nodeType.slice(0, dash)).toLowerCase();
}
/**
* The AWS machine-type family of `nodeType` (plan `2026-10-14-aws-dp-parity`, D9).
*
* An AWS node type is the EC2 instance type with its last dot as a hyphen (`m7i-xlarge` for
* `m7i.xlarge`; `ec2InstanceTypeOf` in provisioning-svc maps it back). So the family is the text
* before the LAST hyphen: `m7i-xlarge` gives `m7i`, `c7i-8xlarge` gives `c7i`, and
* `m7i-flex-large` gives `m7i-flex`. A string with no hyphen is the whole family. The result is
* lowercased, as {@link gcpMachineTypeFamily} lowercases.
*
* Who calls it: {@link resolveMachineType}, through `options.family`, for an AWS app pool. The
* operator and provisioning-svc pass it with {@link MACHINE_TYPE_FAMILY_LADDERS_AWS}.
*
* Input: any string. Output: a string, possibly empty (`''` for `''` and for `'-x'`). Errors: none;
* it never throws. It uses no regex and allocates one slice, so it is ReDoS-free. Pure.
*
* The EC2 form with a dot (`m7i.xlarge`, the kubelet's `node.kubernetes.io/instance-type` label)
* is not a node type. It comes back whole and lowercased, so it matches no rung and resolves to
* nothing.
*/
function awsMachineTypeFamily(nodeType) {
	const dash = nodeType.lastIndexOf("-");
	return (dash === -1 ? nodeType : nodeType.slice(0, dash)).toLowerCase();
}
/**
* Why a resolution was refused. SIX members.
*
* - `unknown-class`               — `ask.class` is not a {@link ComputeClass}.
* - `invalid-ask`                 — the ask is structurally unusable: a non-finite or negative
*                                   vector component, a bad/missing `gpuCount` on `gpu-training`,
*                                   a `gpuCount` on a non-training class, an `accelerator` on a
*                                   non-GPU class, or an unknown `arch`. ONE member with a
*                                   `detail` naming the field, rather than a member per field —
*                                   these are all "the caller sent something malformed", they
*                                   share one remedy (fix the manifest), and a taxonomy nobody
*                                   renders separately is a taxonomy that rots.
* - `class-unavailable-in-region` — no family in the class's ladder exists in this catalog at
*                                   all. DISTINCT from the next one by design (D1): the remedy
*                                   is a platform-operator action (enable a machine type / move
*                                   the tenant), not an app change. `us-west2-a` has no memory
*                                   family and no GPU family whatsoever.
* - `vector-exceeds-class`        — families ARE present, but no shape in them satisfies the ask.
*                                   For `gpu-training` the unsatisfied dimension is the GPU
*                                   count. The remedy is an app change (shrink the ask) or a
*                                   bigger class.
* - `accelerator-unavailable`     — the `accelerator` pin narrowed the candidate set to empty
*                                   while the unpinned set was non-empty.
* - `arch-unavailable`            — the `arch` trait narrowed the ladder to empty while the
*                                   unfiltered ladder was non-empty. The symmetric twin of
*                                   `accelerator-unavailable`: reporting it as
*                                   `class-unavailable-in-region` would send an operator to
*                                   enable machine types that can never exist (there is no
*                                   arm64 family in the `M` ladder on any GCP region).
*
* ## ⛔ PROMOTION AND REFUSAL ARE DIFFERENT OUTCOMES — D15 / D39(d), and the line is the ask
*
* The model note's §4c wording ("a vector exceeding the class ceiling auto-promotes within tier")
* predates D15 and is not the rule. The rule is a SPLIT, decided by whether the class can serve the
* ask at all:
*
*   - **A SMALLER shape in the chosen family was outgrown ⇒ PROMOTION.** The resolver moves up to a
*     shape that fits, emits the `promoted` note, and **the deploy PROCEEDS**. This is the ordinary
*     case and it is not a failure: the worked example (class `C`, 16 vCPU, 200 GiB → `c4-highmem-32`)
*     is a promotion, with the over-provisioned CPU visible in the deploy record.
*   - **NO shape in the class satisfies the ask ⇒ REFUSAL `vector-exceeds-class`, and the deploy does
*     NOT proceed.** There is nothing to promote to. The remedy is an app change (shrink the ask) or a
*     bigger class — and for an explicitly declared class the resolver will NOT substitute a different
*     one (D15: an explicit `C` above 8 GiB/vCPU refuses rather than silently becoming `M`, because
*     declared intent is never discarded).
*
* ⚠️ WHAT SURVIVES FROM §4c, BECAUSE IT IS STILL TRUE AND STILL LOAD-BEARING: neither outcome is a
* **PUBLISH** reject. Publish validation is SHAPE-only (D1) — it never contacts a data plane, so it
* cannot know a region's inventory — and *"this repo has already been bitten by a stale publish-time
* allowlist blocking the family the platform had just fixed."* ⇒ `vector-exceeds-class` is a
* RECONCILE-time refusal reported through the diagnostics surface, never a door that stops a publish.
*
* ⭐ THE CONFLATION TO AVOID: "never a publish reject" is NOT "never a refusal". The original §4c
* sentence ran the two together, which is how a false "an oversized vector always promotes" claim
* reached three docs files and this docblock — sitting thirteen lines below the
* `vector-exceeds-class` member it contradicted. Publish-time and reconcile-time are different doors
* with different information; a claim about one says nothing about the other.
*/
const MACHINE_TYPE_REFUSAL_REASONS = [
	"unknown-class",
	"invalid-ask",
	"class-unavailable-in-region",
	"vector-exceeds-class",
	"accelerator-unavailable",
	"arch-unavailable"
];
/** True iff `r` is a known {@link MachineTypeRefusalReason}. */
function isMachineTypeRefusalReason(r) {
	return typeof r === "string" && MACHINE_TYPE_REFUSAL_REASONS.includes(r);
}
/**
* Facts about a SUCCESSFUL resolution the deploy record must surface (D12: an operator should be
* able to answer "why is this pod on this machine" from the screen alone).
*
* - `derived-class-m`     — the ask was class `N` (declared or defaulted) with a memory:vCPU
*                           ratio above {@link MEM_MIB_PER_VCPU_MAX_N_C}, so `M` was selected.
* - `promoted`            — at least one SMALLER shape in the chosen family was rejected for
*                           insufficient capacity before the winner was picked. This is
*                           "promoted within tier": the ask moved the unit up its class.
* - `generation-fallback` — the chosen family is NOT the first family of the ladder that exists
*                           in this catalog, i.e. the newest generation could not serve the ask
*                           (or is absent here). Kept SEPARATE from `promoted` because it is a
*                           different fact with a different owner: `promoted` is about the app's
*                           ask, `generation-fallback` is about this region's inventory.
* - `accelerator-pinned` / `arch-pinned` — a trait narrowed the candidate set. Present so a
*                           reader can tell "we chose g2 because you pinned nvidia-l4" from
*                           "we chose g2 because g4 is not here".
*/
const MACHINE_TYPE_RESOLUTION_NOTES = [
	"derived-class-m",
	"promoted",
	"generation-fallback",
	"accelerator-pinned",
	"arch-pinned"
];
/**
* ⭐ **`note → the sentence a human reads` — THE D12 PAYLOAD (D51).** TOTAL over
* {@link MACHINE_TYPE_RESOLUTION_NOTES}.
*
* D12's definition of done is that *"an operator should be able to answer 'why is this pod on this
* machine' from the screen alone."* These five sentences ARE that answer. Before D51 the notes shipped
* as slugs with **no producer for their prose**, so every rendering surface would have had to invent
* the wording — and D39(d) draws the `promoted` / `generation-fallback` distinction precisely enough
* (the app's ask vs the region's inventory) that a second, independently-worded definition would
* drift. That is WHY no other file may write these strings.
*
* ⛔ RENDER BY KEY OFF THIS MAP — never a local `Record`, never a `switch`, never a slug prettifier.
* This is hard-rule 14's FULFILMENT, not an exception to it: a total contracts map read by key is
* exactly what that rule asks for.
*
* ⚠️ THEY ARE **READ** VALUES, NOT SENT ONES — the same split `WITHHOLD_REMEDY` documents. The note
* SLUGS (`'promoted'`, …) are the wire vocabulary and renaming one is an API break; these sentences
* are display text, and improving the wording is a copy edit that needs no coordination. ⛔ Do not
* "harmonise" the two: nothing anywhere compares a sentence to anything.
*
* ⚠️ Each sentence is written to stand ALONE beside a resolved machine type, because that is how it is
* rendered — no "because" prefix to be double-prefixed, no trailing period joined into a list, and no
* reference to a sibling note (a resolution can carry several at once, in enum order).
*/
const MACHINE_TYPE_RESOLUTION_NOTE_SENTENCE = {
	"derived-class-m": "The platform selected the memory class: this vector asks for more than 8 GiB per vCPU, which no standard or compute shape sells.",
	promoted: "The platform moved up to a larger shape in the same family: the smaller ones could not hold this vector.",
	"generation-fallback": "The platform used an older generation in this class: the newer families are not enabled on this data plane.",
	"accelerator-pinned": "The fixture pinned an accelerator model, so the platform chose among the shapes carrying it rather than the newest available.",
	"arch-pinned": "The fixture requested a CPU architecture, so the platform chose among the families offering it rather than the newest available."
};
/** Longest untrusted fragment {@link safeLabel} will echo into a `detail`. */
const DETAIL_LABEL_MAX = 64;
/**
* Render an UNTRUSTED value for a `detail` sentence.
*
* ⛔ THIS IS THE NUL DEFENCE (security-audit §3, and the 2026-08-08 lesson). `ask.class`,
* `ask.accelerator` and `ask.arch` are app-authored and arrive as `unknown`. Echoing one raw into
* a `detail` puts a caller-controlled string into a log line, a NATS event payload and an HTTP
* response body — so a `\x00`, a `\r\n`, an ANSI escape or a Unicode direction override becomes
* a log-forging / terminal-spoofing primitive (CodeQL `js/log-injection`).
*
* Everything outside printable ASCII is replaced with `?`, then the result is truncated. A
* character-class replace is LINEAR — no alternation, no nesting, no backtracking — so this is
* also ReDoS-free.
*
* A non-string renders as `<typeof>` rather than `String(v)`, because `String(v)` on an object
* with a hostile `toString` re-opens the hole this function closes.
*/
function safeLabel(v) {
	if (typeof v !== "string") return `<${typeof v}>`;
	const cleaned = v.replace(/[^\x20-\x7E]/g, "?");
	return cleaned.length > DETAIL_LABEL_MAX ? `${cleaned.slice(0, DETAIL_LABEL_MAX)}...` : cleaned;
}
/** Render an untrusted NUMBER for a `detail`. `String(NaN)`/`String(Infinity)` are safe and
*  informative; a non-number renders as its `typeof`, for the same `toString` reason as above. */
function safeNumber(v) {
	return typeof v === "number" ? String(v) : `<${typeof v}>`;
}
/** A finite, non-negative number. `NaN`, `±Infinity` and negatives all fail. */
function isFiniteNonNegative(n) {
	return typeof n === "number" && Number.isFinite(n) && n >= 0;
}
/** A positive safe integer, at most {@link MAX_GPU_COUNT}. */
function isValidGpuCount(n) {
	return typeof n === "number" && Number.isSafeInteger(n) && n >= 1 && n <= 8;
}
function refuse(reason, computeClass, detail) {
	return {
		ok: false,
		reason,
		computeClass,
		detail
	};
}
/**
* The candidate ordering — SMALLEST FIRST, and total.
*
* `(cpuMilliCeiling, memoryMiBCeiling, nodeType)` for a cpu/mem ask;
* `(gpu.count, cpuMilliCeiling, memoryMiBCeiling, nodeType)` for `gpu-training`, because there
* the GPU count is the dimension being satisfied.
*
* ⭐ The `nodeType` tiebreak is not cosmetic: without it, two shapes with identical ceilings
* resolve by the catalog's ARRAY ORDER, which comes from a cloud API response and is not stable.
* A resolver whose answer depends on response ordering produces a pod that moves machine type
* between reconciles for no declared reason. `localeCompare` is avoided deliberately — it is
* locale-sensitive, and machine-type names are ASCII.
*/
function compareCandidates(a, b, byGpuCount) {
	if (byGpuCount) {
		const ga = a.gpu?.count ?? 0;
		const gb = b.gpu?.count ?? 0;
		if (ga !== gb) return ga - gb;
	}
	if (a.cpuMilliCeiling !== b.cpuMilliCeiling) return a.cpuMilliCeiling - b.cpuMilliCeiling;
	if (a.memoryMiBCeiling !== b.memoryMiBCeiling) return a.memoryMiBCeiling - b.memoryMiBCeiling;
	return a.nodeType < b.nodeType ? -1 : a.nodeType > b.nodeType ? 1 : 0;
}
/** Group the catalog by `familyOf`. A `Map`, so a family name that happens to collide with an
*  `Object.prototype` member cannot resolve to anything. */
function groupByFamily(entries, familyOf) {
	const out = /* @__PURE__ */ new Map();
	for (const e of entries) {
		const family = familyOf(e.nodeType);
		const bucket = out.get(family);
		if (bucket === void 0) out.set(family, [e]);
		else bucket.push(e);
	}
	return out;
}
/**
* ⭐ **THE D1 RESOLVER.** Resolve a class + vector (+ traits) to a concrete machine type from a
* data plane's enabled catalog, or return a typed refusal.
*
* ## The floor rule (model note §4c — the load-bearing semantics)
*
* The ask's `(cpu, mem)` vector is a **MINIMUM**. The resolver walks the class's family ladder
* NEWEST-FIRST and returns the SMALLEST shape in the first family that satisfies BOTH
* dimensions. No app ever declares a cpu:mem ratio; the `-highcpu`/`-standard`/`-highmem` shape
* is an implementation detail exactly like the generation.
*
* The worked example, which is a named test (`compute-class-resolver.test.ts`): class `C`,
* 16 vCPU, 200 GiB — a 1:12.5 ratio no family sells — resolves to the `c4-highmem-32` shape
* (32 vCPU / 248 GB): both dimensions satisfied, current generation, and the over-provisioned CPU
* is visible in the deploy record through {@link ResolvedMachineType.cpuMilliCeiling} plus the
* `promoted` note.
*
* ## ⛔ D10 — DO NOT CALL THIS FOR AN UNDECLARED UNIT
*
* A unit that declares NO class, NO sizing and NO resources must deploy EXACTLY as today
* (platform default resources, default-app pool) — that is D10, and a render-parity test in
* 30-operator measures it. This function given a zero vector will happily return the SMALLEST
* `N` shape in the catalog, which is a machine-type nodeSelector where today there is none.
* ⇒ **The caller decides whether to resolve at all.** This function has no "declared nothing"
* concept and must not grow one: an "if everything is empty, return no placement" arm here would
* put the D10 decision in the wrong layer and make the resolver's return type a three-way union.
*
* ## Totality (security-audit §3)
*
* TOTAL over hostile input: every field of `ask` is accepted as `unknown`-shaped, no input path
* throws, and every refusal is typed. `detail` strings are sanitised by {@link safeLabel}. The
* only string ever RETURNED as `nodeType` is copied verbatim from a catalog entry, so the output
* cannot become an injection vector into the node-label/taint sink (security-audit §4).
*
* ## Purity
*
* No I/O, no clock, no randomness, no mutation of `ask` or `zoneCatalog`. Same inputs ⇒ same
* output, which is what lets fixture-deployer, worker-lifecycle and the CU broker share ONE
* implementation (D1) and what lets the whole thing be tested to 100% without a cluster.
*
* ## The cloud (plan `2026-10-14-aws-dp-parity`, D9)
*
* `options` selects the cloud. Absent, or with both members absent, the resolver walks
* {@link MACHINE_TYPE_FAMILY_LADDERS_GCP} and reads families with {@link gcpMachineTypeFamily}, so
* every result is byte-identical to the resolver before `options` existed (D14). The resolver
* cannot see the cloud, so the per-cloud default of D9 is the caller's duty. Every caller that
* resolves against the catalog of an `aws` data plane must pass
* `{ ladders: MACHINE_TYPE_FAMILY_LADDERS_AWS, family: awsMachineTypeFamily }`: the app-pool
* catalog, `resolveCompute` (fixtures, pool runners, isolated pools, workers) and the compute-unit
* broker. Without the pair, an AWS catalog holds no GCP family, and every class refuses
* `class-unavailable-in-region` with a GCP family list. Each member defaults on its own.
*
* The arch trait reads {@link ARM64_MACHINE_FAMILIES_GCP} on every cloud. No AWS rung is in that
* list, so on AWS `arch: 'x86'` narrows nothing and `arch: 'arm64'` refuses `arch-unavailable`
* when the class has a family in the catalog.
*
* An empty ladder refuses `class-unavailable-in-region`, with a `detail` that says the class has
* no machine family on this cloud. No shipped ladder is empty; a caller's own `options.ladders` can
* hold one.
*
* `options` is platform code, never app input: `ladders` must hold a ladder for every class, and
* `family` must be total and pure. The resolver does not check either.
*
* @param ask         what the app + tenant multiplier want. Untrusted.
* @param zoneCatalog this DP's enabled machine types, optionally zone-scoped.
* @param options     the ladders and the family function of the cloud. Absent means GCP.
*/
function resolveMachineType(ask, zoneCatalog, options) {
	const ladders = options?.ladders ?? MACHINE_TYPE_FAMILY_LADDERS_GCP;
	const familyOf = options?.family ?? gcpMachineTypeFamily;
	const declared = ask.class;
	if (declared !== void 0 && !isComputeClass(declared)) return refuse("unknown-class", null, `class '${safeLabel(declared)}' is not a compute class (expected one of: ${COMPUTE_CLASSES.join(", ")})`);
	const declaredClass = declared ?? null;
	const baseClass = declaredClass ?? "N";
	const cpuMilli = ask.cpuMilli ?? 0;
	const memoryMiB = ask.memoryMiB ?? 0;
	if (!isFiniteNonNegative(cpuMilli) || !isFiniteNonNegative(memoryMiB)) return refuse("invalid-ask", baseClass, `the resource vector must be finite and non-negative (cpuMilli=${safeNumber(ask.cpuMilli)}, memoryMiB=${safeNumber(ask.memoryMiB)})`);
	const byGpuCount = baseClass === "gpu-training";
	if (byGpuCount) {
		if (!isValidGpuCount(ask.gpuCount)) return refuse("invalid-ask", baseClass, `class 'gpu-training' requires an integer gpuCount between 1 and 8 (got ${safeNumber(ask.gpuCount)}); the cpu/mem vector is not used for this class because GPU count is fixed per training shape`);
	} else if (ask.gpuCount !== void 0) return refuse("invalid-ask", baseClass, `gpuCount is only meaningful for class 'gpu-training' (this ask is class '${baseClass}'); for 'gpu-inference' the accelerator count comes from the resolved shape`);
	if (ask.accelerator !== void 0 && !isGpuComputeClass(baseClass)) return refuse("invalid-ask", baseClass, `an accelerator pin is only meaningful for a GPU class (this ask is class '${baseClass}')`);
	const notes = [];
	let computeClass = baseClass;
	if (baseClass === "N" && memoryMiB * 1e3 > 8192 * cpuMilli) {
		computeClass = "M";
		notes.push("derived-class-m");
	}
	const ladder = ladders[computeClass];
	let archFamilies = ladder;
	if (ask.arch !== void 0) {
		if (!isComputeArch(ask.arch)) return refuse("invalid-ask", computeClass, `arch '${safeLabel(ask.arch)}' is not a known architecture (expected one of: ${COMPUTE_ARCHES.join(", ")})`);
		archFamilies = ask.arch === "arm64" ? ladder.filter((f) => ARM64_FAMILY_SET.has(f)) : ladder.filter((f) => !ARM64_FAMILY_SET.has(f));
		notes.push("arch-pinned");
	}
	const zone = zoneCatalog.zone;
	const zoneScoped = zone === null ? zoneCatalog.entries : zoneCatalog.entries.filter((e) => {
		const zones = e.gpu?.zones;
		return zones === void 0 || zones.includes(zone);
	});
	const accelerator = ask.accelerator;
	const pinned = accelerator === void 0 ? zoneScoped : zoneScoped.filter((e) => e.gpu?.acceleratorType === accelerator);
	if (accelerator !== void 0) notes.push("accelerator-pinned");
	const byFamilyUnpinned = groupByFamily(zoneScoped, familyOf);
	const byFamilyPinned = groupByFamily(pinned, familyOf);
	const presentLadder = ladder.filter((f) => byFamilyUnpinned.has(f));
	const presentArch = archFamilies.filter((f) => byFamilyUnpinned.has(f));
	const presentFinal = archFamilies.filter((f) => byFamilyPinned.has(f));
	if (presentFinal.length === 0) {
		if (presentArch.length > 0) return refuse("accelerator-unavailable", computeClass, `no machine type in class '${computeClass}' carries accelerator '${safeLabel(accelerator)}' on this data plane`);
		if (presentLadder.length > 0) return refuse("arch-unavailable", computeClass, `class '${computeClass}' has no ${safeLabel(ask.arch)} machine family available on this data plane`);
		return refuse("class-unavailable-in-region", computeClass, ladder.length === 0 ? `class '${computeClass}' has no machine family on this cloud` : `class '${computeClass}' has no machine family enabled on this data plane (a platform operator must enable one of: ${ladder.join(", ")})`);
	}
	const newestPresent = presentFinal[0];
	for (const family of presentFinal) {
		const candidates = [...byFamilyPinned.get(family)].sort((a, b) => compareCandidates(a, b, byGpuCount));
		let rejectedSmaller = 0;
		for (const entry of candidates) {
			if (!(byGpuCount ? entry.gpu !== void 0 && entry.gpu.count >= ask.gpuCount : (!isGpuComputeClass(computeClass) || entry.gpu !== void 0) && entry.cpuMilliCeiling >= cpuMilli && entry.memoryMiBCeiling >= memoryMiB)) {
				rejectedSmaller++;
				continue;
			}
			if (rejectedSmaller > 0) notes.push("promoted");
			if (family !== newestPresent) notes.push("generation-fallback");
			return {
				ok: true,
				nodeType: entry.nodeType,
				computeClass,
				declaredClass,
				family,
				cpuMilliCeiling: entry.cpuMilliCeiling,
				memoryMiBCeiling: entry.memoryMiBCeiling,
				gpu: entry.gpu === void 0 ? null : {
					acceleratorType: entry.gpu.acceleratorType,
					count: entry.gpu.count
				},
				bootDiskType: platformChosenBootDiskType(entry.nodeType),
				notes: MACHINE_TYPE_RESOLUTION_NOTES.filter((n) => notes.includes(n))
			};
		}
	}
	return refuse("vector-exceeds-class", computeClass, byGpuCount ? `no machine type in class '${computeClass}' on this data plane offers ${ask.gpuCount} GPUs (largest available families: ${presentFinal.join(", ")})` : `no machine type in class '${computeClass}' on this data plane satisfies ${cpuMilli}m CPU and ${memoryMiB}MiB memory (families available here: ${presentFinal.join(", ")})`);
}
//#endregion
export { ARM64_MACHINE_FAMILIES_GCP, AWS_MACHINE_TYPE_FAMILIES, COMPUTE_ARCHES, COMPUTE_CLASSES, DEFAULT_COMPUTE_CLASS, DEFAULT_FIXTURE_SIZING, DEFAULT_SIZE, GPU_COMPUTE_CLASSES, GPU_MACHINE_FAMILIES_AWS, GPU_MACHINE_FAMILIES_GCP, MACHINE_TYPE_FAMILY_LADDERS, MACHINE_TYPE_FAMILY_LADDERS_AWS, MACHINE_TYPE_FAMILY_LADDERS_GCP, MACHINE_TYPE_REFUSAL_REASONS, MACHINE_TYPE_RESOLUTION_NOTES, MACHINE_TYPE_RESOLUTION_NOTE_SENTENCE, MAX_GPU_COUNT, MEM_MIB_PER_VCPU_MAX_N_C, NEVER_SPOT_MACHINE_FAMILIES, SIZES, SIZE_MULTIPLIERS, SIZING_MODES, TIER_MACHINE_FAMILIES, awsMachineTypeFamily, computeArchSchema, computeClassSchema, fixtureSizingSchema, gcpMachineTypeFamily, isComputeArch, isComputeClass, isGpuComputeClass, isMachineTypeRefusalReason, isNeverSpotFamily, isSize, isTierMachineFamily, multiplierForSize, resolveMachineType, sizeSchema };

//# sourceMappingURL=compute-class.js.map